// src/components/SubmitTime/hooks/useValidation.ts
// Custom hook for form validation logic
// Migrated from SubmitTime.js validation methods

import { useCallback, useState } from 'react';
import type { ValidationState, TimeEvent } from '../types';

export interface ValidationHelpers {
  valForm: ValidationState;
  setValForm: (valForm: ValidationState) => void;
  updateValidation: (field: keyof ValidationState, value: boolean) => void;
  validateMinutesPerDateAdd: (
    date: string,
    minutesToAdd: number,
    timeTracking: TimeEvent[],
    maxMinutesPerDay: number
  ) => boolean;
  validateTimeFormat: (timeString: string) => boolean;
  validateHoursNotZero: (hours: string) => boolean;
  validateHoursRange: (hours: string, timeTrackingTeam: boolean) => boolean;
  resetValidation: (typesubmittime: 1 | 2) => void;
}

const initialValidationState: ValidationState = {
  estimated: false,
  date: false,
  hours: false,
  hoursOutOfRange: false,
  remaining: false,
  description: false,
  selectedId: false,
  remeaning: false,
  maxHours: false,
  disabledLayers: false,
  showPhases: false,
  validPhases: false,
  disabledPhases: false,
  showProjects: false,
  validProject: false,
  disabledProject: false,
  showType: false,
  validType: false,
  disabledType: false,
  showTask: false,
  validTask: false,
  disabledTask: false,
};

export function useValidation(): ValidationHelpers
{
  const [valForm, setValForm] = useState<ValidationState>(initialValidationState);

  // Update a single validation field
  const updateValidation = useCallback((field: keyof ValidationState, value: boolean) =>
  {
    setValForm(prev => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  // Validate that adding minutes to a date doesn't exceed max minutes per day
  const validateMinutesPerDateAdd = useCallback(
    (
      date: string,
      minutesToAdd: number,
      timeTracking: TimeEvent[],
      maxMinutesPerDay: number,
    ): boolean =>
    {
      if (!date) return false;

      const targetDate = new Date(date);
      const targetDateStr = targetDate.toISOString().split('T')[0];

      // Sum existing minutes for this date
      const existingMinutes = timeTracking
        .filter(event =>
        {
          const eventDate = new Date(event.Date);
          const eventDateStr = eventDate.toISOString().split('T')[0];
          return eventDateStr === targetDateStr;
        })
        .reduce((sum, event) => sum + (event.Minutes || 0), 0);

      const totalMinutes = existingMinutes + minutesToAdd;

      return totalMinutes <= maxMinutesPerDay;
    },
    [],
  );

  // Validate time format (HH:MM)
  const validateTimeFormat = useCallback((timeString: string): boolean =>
  {
    if (!timeString) return false;

    const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
    return timeRegex.test(timeString);
  }, []);

  // Validate hours is not 0:00
  const validateHoursNotZero = useCallback((hours: string): boolean =>
  {
    if (!hours) return false;

    const parts = hours.split(':');
    const h = parseInt(parts[0] ?? '0') || 0;
    const m = parseInt(parts[1] ?? '0') || 0;

    return h > 0 || m > 0;
  }, []);

  // Validate the hours entered by the user are within the allowed range
  const validateHoursRange = useCallback((hours: string, timeTrackingTeam: boolean): boolean =>
  {
    if (!hours) return true; // Empty is validated by validateHoursNotZero

    let hoursValue = 0;
    if (hours.includes(':'))
    {
      const parts = hours.split(':');
      const hoursPart = parseInt(parts[0] ?? '0') || 0;
      const minutesPart = parseInt(parts[1] ?? '0') || 0;
      hoursValue = hoursPart + minutesPart / 60;
    }
    else
    {
      hoursValue = parseFloat(hours) || 0;
    }

    if (hoursValue < 0) return false;

    // The limit itself is not allowed: 24 (or 250) or more is invalid
    const maxHours = timeTrackingTeam ? 250 : 24;
    return hoursValue < maxHours;
  }, []);

  // Reset validation to initial state
  const resetValidation = useCallback((typesubmittime: 1 | 2) =>
  {
    setValForm({
      estimated: false,
      date: false,
      hours: false,
      hoursOutOfRange: false,
      remaining: false,
      description: false,
      selectedId: false,
      remeaning: false,
      maxHours: false,
      disabledLayers: false,
      showPhases: false,
      validPhases: false,
      disabledPhases: false,
      showProjects: typesubmittime === 2,
      validProject: false,
      disabledProject: false,
      showType: typesubmittime === 2,
      validType: false,
      disabledType: true,
      showTask: typesubmittime === 2,
      validTask: false,
      disabledTask: true,
    });
  }, []);

  return {
    valForm,
    setValForm,
    updateValidation,
    validateMinutesPerDateAdd,
    validateTimeFormat,
    validateHoursNotZero,
    validateHoursRange,
    resetValidation,
  };
}
